import { Component, EventEmitter, Output, ViewChild, ElementRef, AfterViewInit } from '@angular/core';

/**
 * Reusable signature capture: draw on a canvas, or type a name in one of a few
 * signature fonts. Emits the signature as a PNG data URL (or null when cleared).
 */
@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.css'],
})
export class SignaturePadComponent implements AfterViewInit {
  @Output() signatureChange = new EventEmitter<string | null>();
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  mode: 'draw' | 'type' = 'draw';
  typedName = '';
  fontIdx = 0;
  readonly fonts = [
    { label: 'Cursive', css: "'Segoe Script','Bradley Hand','Brush Script MT',cursive" },
    { label: 'Elegant', css: "'Palatino Linotype','Book Antiqua',Palatino,serif" },
    { label: 'Casual',  css: "'Comic Sans MS','Comic Sans',cursive" },
    { label: 'Formal',  css: "'Times New Roman',Times,serif" },
  ];

  private ctx!: CanvasRenderingContext2D;
  private drawing = false;
  private hasInk = false;

  ngAfterViewInit(): void {
    const c = this.canvasRef.nativeElement;
    this.ctx = c.getContext('2d')!;
    this.ctx.lineWidth = 2.2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = '#152238';
  }

  setMode(m: 'draw' | 'type'): void { this.mode = m; this.clear(); }

  private pos(e: PointerEvent) {
    const r = this.canvasRef.nativeElement.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  onDown(e: PointerEvent): void {
    if (this.mode !== 'draw') return;
    e.preventDefault();
    this.drawing = true;
    const p = this.pos(e);
    this.ctx.beginPath();
    this.ctx.moveTo(p.x, p.y);
  }
  onMove(e: PointerEvent): void {
    if (!this.drawing) return;
    const p = this.pos(e);
    this.ctx.lineTo(p.x, p.y);
    this.ctx.stroke();
    this.hasInk = true;
  }
  onUp(): void {
    if (!this.drawing) return;
    this.drawing = false;
    this.emit();
  }

  renderTyped(): void {
    const c = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, c.width, c.height);
    const name = this.typedName.trim();
    if (!name) { this.hasInk = false; this.signatureChange.emit(null); return; }
    this.ctx.fillStyle = '#152238';
    this.ctx.textBaseline = 'middle';
    let size = 52;
    do {
      this.ctx.font = `italic ${size}px ${this.fonts[this.fontIdx].css}`;
      size -= 2;
    } while (this.ctx.measureText(name).width > c.width - 24 && size > 14);
    this.ctx.fillText(name, 16, c.height / 2);
    this.hasInk = true;
    this.emit();
  }

  pickFont(i: number): void { this.fontIdx = i; this.renderTyped(); }

  clear(): void {
    const c = this.canvasRef.nativeElement;
    this.ctx?.clearRect(0, 0, c.width, c.height);
    this.hasInk = false;
    this.typedName = '';
    this.signatureChange.emit(null);
  }

  private emit(): void {
    this.signatureChange.emit(this.hasInk ? this.canvasRef.nativeElement.toDataURL('image/png') : null);
  }
}
